import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  ClaimsPreview,
  RecordClaimsResult,
  RecordedClaim,
  RecordedClaimDetail,
  RecordedRemittance,
  RecordedRemittanceDetail,
  RemittancePreview,
  RemittanceXeroLists,
  RemittanceXeroSettings,
  SaveRemittanceXeroSettings,
  SettlementFile,
  VoidedRemittance,
} from '../types/retailerSettlement';

/**
 * A retailer's rebate claims and remittance advices. Backend: HHNodeServer
 * routes/nurseryReceivables.js (`/retailer-settlement/...`).
 * Plan: docs/bunnings-remittance-plan.md.
 *
 * Any Accounts seat checks and reads; recording and voiding need the Accounts
 * Admin seat. A remittance is checked against the claims recorded for its head
 * office, so the month's claims are recorded first.
 */

// Every line is looked up against the invoices; give a large file time.
const SLOW = { timeout: 120_000 };

const remittanceBody = (fileName: string, dataBase64: string) => ({ file_name: fileName, data_base64: dataBase64 });

class RetailerSettlementService {
  /** Check rebate claim CSVs against our invoices. Writes nothing. */
  async previewClaims(files: SettlementFile[]): Promise<ApiResponse<ClaimsPreview>> {
    return apiService.post('/nursery/retailer-settlement/claims/preview', { files }, undefined, SLOW);
  }

  /** Record rebate claims: all of them or none. */
  async recordClaims(files: SettlementFile[]): Promise<ApiResponse<RecordClaimsResult>> {
    return apiService.post('/nursery/retailer-settlement/claims', { files }, undefined, SLOW);
  }

  async listClaims(): Promise<ApiResponse<RecordedClaim[]>> {
    return apiService.get('/nursery/retailer-settlement/claims');
  }

  async getClaim(id: number): Promise<ApiResponse<RecordedClaimDetail>> {
    return apiService.get(`/nursery/retailer-settlement/claims/${id}`);
  }

  async voidClaim(id: number, reason: string): Promise<ApiResponse<unknown>> {
    return apiService.post(`/nursery/retailer-settlement/claims/${id}/void`, { reason });
  }

  /** Check a remittance advice (.xls, .xlsx or .csv, as base64) against the recorded claims. Writes nothing. */
  async previewRemittance(fileName: string, dataBase64: string): Promise<ApiResponse<RemittancePreview>> {
    return apiService.post('/nursery/retailer-settlement/remittances/preview', remittanceBody(fileName, dataBase64), undefined, SLOW);
  }

  /** Record a remittance: a payment across its invoices, and the claims settled out of it. */
  async recordRemittance(fileName: string, dataBase64: string): Promise<ApiResponse<RecordedRemittanceDetail>> {
    return apiService.post('/nursery/retailer-settlement/remittances', remittanceBody(fileName, dataBase64), undefined, SLOW);
  }

  async listRemittances(): Promise<ApiResponse<RecordedRemittance[]>> {
    return apiService.get('/nursery/retailer-settlement/remittances');
  }

  async getRemittance(id: number): Promise<ApiResponse<RecordedRemittanceDetail>> {
    return apiService.get(`/nursery/retailer-settlement/remittances/${id}`);
  }

  /** Voided here, and its payments deleted in Xero; `xero_to_do` names what Xero's API cannot remove. */
  async voidRemittance(id: number, reason: string): Promise<ApiResponse<VoidedRemittance>> {
    return apiService.post(`/nursery/retailer-settlement/remittances/${id}/void`, { reason }, undefined, SLOW);
  }

  // ── Xero (phase 3) ──

  async getXeroSettings(): Promise<ApiResponse<RemittanceXeroSettings>> {
    return apiService.get('/nursery/retailer-settlement/xero');
  }

  async saveXeroSettings(data: SaveRemittanceXeroSettings): Promise<ApiResponse<RemittanceXeroSettings>> {
    return apiService.put('/nursery/retailer-settlement/xero', data);
  }

  async getXeroLists(): Promise<ApiResponse<RemittanceXeroLists>> {
    return apiService.get('/nursery/retailer-settlement/xero/lists', { timeout: 30_000 });
  }

  /** Send a claim to Xero: a failed one again, or one recorded before posting was on. */
  async sendClaimToXero(id: number): Promise<ApiResponse<RecordedClaimDetail>> {
    return apiService.post(`/nursery/retailer-settlement/claims/${id}/xero`, {});
  }

  async sendRemittanceToXero(id: number): Promise<ApiResponse<RecordedRemittanceDetail>> {
    return apiService.post(`/nursery/retailer-settlement/remittances/${id}/xero`, {});
  }
}

export default new RetailerSettlementService();
